// Trendlines V3 on the chart (DWLF-184, admin beta). Lines through the confirmed cycle pivots the
// chart already has, computed by the indicators library over the FULL candles of the shown timeframe
// (so a short visible window keeps the history a line was born from) and clipped to the window.
// The chart's price axis is linear, so the lines are fitted in linear price and draw straight; the
// library's angle and fan thresholds are then a share of price, so they mean the same at any price.
// A line is drawn from the bar it became knowable to the bar it broke (or the last bar); the stretch
// from its first anchor to that birth is drawn faint, because it was not yet known there.
import { CycleTrendlines } from '@andywilliams/indicators';

export const CYCLE_TRENDLINE_TIMEFRAMES = Object.freeze(['daily', 'weekly']);

export const CYCLE_TRENDLINE_PARAMS = Object.freeze({ scale: 'linear' });

// Distinct from the existing trendlines (blue up, red down) so the two can be compared side by side.
export const CYCLE_TRENDLINE_COLORS = Object.freeze({ support: '#FFB300', resistance: '#E040FB' });

const finite = (value) => Number.isFinite(value);

/**
 * The chart's confirmed pivots (`useBackendCycleEvents` rows) → the library's pivot rows, dated at
 * the later of the row's `confirmTime` and its emission (`eventTimestamp`). The confirm can be a
 * retrospective component bar, earlier than the pivot was published (and on the pivot bar itself
 * in the worst case); the emission is never earlier than the confirm, and a pivot has one stored
 * row per timeframe, so the later of the two is the bar it was knowable (DWLF-184).
 */
export const cycleTrendlineRows = (lows, highs) => [
  ...(lows || []).map((pivot) => ({ kind: 'low', pivot, price: pivot?.low })),
  ...(highs || []).map((pivot) => ({ kind: 'high', pivot, price: pivot?.high })),
]
  .filter(({ pivot, price }) => pivot && finite(pivot.pivotTime) && finite(price))
  .map(({ kind, pivot, price }) => ({
    kind,
    pivotTime: pivot.pivotTime,
    confirmTime: Math.max(...[pivot.confirmTime, pivot.emittedAt].filter(finite), Number.NEGATIVE_INFINITY),
    price,
  }));

/**
 * Pivots the library could not place, other than those older than the loaded candles (expected:
 * the chart loads a window). A dropped pivot is absent, so its neighbours pair across it; during
 * the soak whoever judges the lines should know, so they are reported.
 */
export const unexpectedDrops = (dropped, firstT) => (dropped || []).filter(
  ({ row, reason }) => !(reason === 'pivot-bar-missing' && row.pivotTime < firstT),
);

/** The V3 lines for these candles and pivots, or [] when there is nothing to draw. */
export const computeCycleTrendlines = (fullCandles, lows, highs) => {
  const candles = (fullCandles || []).filter((candle) => candle && [candle.t, candle.o, candle.h, candle.l, candle.c].every(finite));
  if (candles.length < 2) {return [];}
  const { pivots, dropped } = CycleTrendlines.toTrendlinePivots(candles, cycleTrendlineRows(lows, highs));
  const unexpected = unexpectedDrops(dropped, candles[0].t);
  if (unexpected.length) {
    // eslint-disable-next-line no-console
    console.warn('cycle trendlines (beta): pivots left out', unexpected.map(({ row, reason }) => ({
      pivot: new Date(row.pivotTime).toISOString().slice(0, 10), kind: row.kind, reason,
    })));
  }
  if (pivots.length < 2) {return [];}
  return CycleTrendlines.detectCycleTrendlines(candles, pivots, { params: CYCLE_TRENDLINE_PARAMS }).lines;
};

const segment = (line, { fromT, toT }, window) => {
  const start = Math.max(fromT, window.from);
  const end = Math.min(toT, window.to);
  if (!(end > start)) {return null;}
  return [
    { t: start, v: CycleTrendlines.trendlineValueAt(line, start) },
    { t: end, v: CycleTrendlines.trendlineValueAt(line, end) },
  ];
};

/**
 * Chart line series for `lines`, clipped to [from, to]. Each line gives a faint pre-birth stretch
 * (first anchor to birth) and its live stretch (birth to break, retirement or the last bar). Base
 * lines are thicker than fans; a broken or retired line is dimmed.
 */
export const cycleTrendlineSeries = (lines, { lastT, from, to }) => (lines || []).flatMap((line) => {
  const color = CYCLE_TRENDLINE_COLORS[line.role];
  const endT = line.breakBarTime ?? line.retiredBarTime ?? lastT;
  const ended = line.breakBarTime !== null || line.retiredBarTime !== null;
  const window = { from, to };
  const preBirth = segment(line, { fromT: line.anchors[0].t, toT: line.birthBarTime }, window);
  const live = segment(line, { fromT: line.birthBarTime, toT: endT }, window);
  return [
    preBirth && {
      key: `cycle-trendline ${line.id} pre-birth`,
      type: 'line',
      data: preBirth,
      style: { color, lineWidth: 1, dashed: true, opacity: 0.45 },
      showInTooltip: false,
    },
    live && {
      key: `cycle-trendline ${line.id}`,
      type: 'line',
      data: live,
      style: { color, lineWidth: line.kind === 'base' ? 2.2 : 1.4, opacity: ended ? 0.55 : 0.95 },
      showInTooltip: false,
    },
  ].filter(Boolean);
});
